/**
 * CreateEventGuestList — Step: Guest list.
 * Verbatim port: look up customers, add new, add segment, import CSV,
 * running total, remove entries.
 */

import { useRef, useState, useEffect, type ChangeEvent, type MouseEvent } from "react";
import { useTenantLabels } from "../../../lib/labels";
import { Avatar, Btn, Card, Field, Icon, Input, T, tx } from "@v0y4ge/jrni-ui";
import { listGuests, listGuestSegments } from "@v0y4ge/api-client";
import { CreationShell } from "./CreationShell";
import { CreationStore, useCreationConfig, isPublicAnyone } from "./store";
import { FormSection, WizardCanvas } from "./wizard-utils";
import { useNav } from "../../../shell/router";
import { useT } from "../../../lib/i18n";

type GuestEntry = { uid: string; kind: "customer" | "new" | "segment"; id?: string; name: string; firstName?: string; lastName?: string; email?: string; company?: string; color?: string; init?: string; count?: number; imported?: boolean };

const uid = () => "gl_" + Date.now().toString(36) + Math.random().toString(36).slice(2, 5);

export default function CreateEventGuestList() {
  const t = useT();
  const nav = useNav();
  const cfg = useCreationConfig();
  useEffect(() => { if (isPublicAnyone(cfg.visibility)) nav.go("create.publish"); }, [cfg.visibility]);

  const list = (cfg.guestList || []) as GuestEntry[];
  const setList = (next: GuestEntry[]) => CreationStore.setField("guestList", next);
  const remove = (u: string) => setList(list.filter((e) => e.uid !== u));

  const [mode, setMode] = useState<"customers" | "new" | "segment" | "import">("customers");
  const lbl = useTenantLabels(); // #1 — "customer" is tenant-overridable vocabulary
  const [q, setQ] = useState("");
  const [newFirst, setNewFirst] = useState("");
  const [newLast, setNewLast] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [importErr, setImportErr] = useState("");
  const fileRef = useRef<HTMLInputElement | null>(null);

  // Real customer/segment lookups — debounced search via listGuests, one-shot
  // listGuestSegments load. No local seed; empty until the API responds.
  const [results, setResults] = useState<GuestEntry[]>([]);
  const [searching, setSearching] = useState(false);
  const searchDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    const term = q.trim();
    if (searchDebounce.current) clearTimeout(searchDebounce.current);
    if (!term) { setResults([]); return; }
    searchDebounce.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await listGuests({ search: term, limit: 8 } as never);
        const raw: Record<string, unknown>[] = (res as { guests?: unknown[]; data?: unknown[] }).guests as Record<string, unknown>[] ?? (res as { data?: unknown[] }).data as Record<string, unknown>[] ?? [];
        setResults(raw.slice(0, 8).map((g) => ({
          uid: "", kind: "customer" as const, id: String(g.id ?? ""),
          name: String(g.name ?? `${g.firstName ?? ""} ${g.lastName ?? ""}`.trim() ?? ""),
          email: g.email ? String(g.email) : undefined,
          company: g.company ? String(g.company) : undefined,
          color: g.color ? String(g.color) : undefined,
          init: g.init ? String(g.init) : undefined,
        })));
      } catch (err) {
        console.warn("listGuests search failed:", err);
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
  }, [q]);

  const [allSegments, setAllSegments] = useState<{ id: string; name: string; count: number; color?: string }[]>([]);
  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const res = await listGuestSegments();
        const raw: Record<string, unknown>[] = (res as { segments?: unknown[]; data?: unknown[] }).segments as Record<string, unknown>[] ?? (res as { data?: unknown[] }).data as Record<string, unknown>[] ?? [];
        if (live) setAllSegments(raw.map((s) => ({ id: String(s.id ?? ""), name: String(s.name ?? "Segment"), count: Number(s.count ?? s.memberCount ?? 0), color: s.color ? String(s.color) : undefined })));
      } catch (err) {
        console.warn("listGuestSegments failed:", err);
        if (live) setAllSegments([]);
      }
    })();
    return () => { live = false; };
  }, []);

  const addedIds = new Set(list.filter((e) => e.kind === "customer").map((e) => e.id));
  const addedSegs = new Set(list.filter((e) => e.kind === "segment").map((e) => e.id));

  const addCustomer = (g: GuestEntry) => {
    if (addedIds.has(g.id)) return;
    setList([...list, { uid: uid(), kind: "customer", id: g.id, name: g.name, email: g.email, company: g.company, color: g.color, init: g.init }]);
  };
  const addNew = () => {
    const first = newFirst.trim(), last = newLast.trim();
    const name = `${first} ${last}`.trim();
    if (!first) return;
    setList([...list, { uid: uid(), kind: "new", name, firstName: first, lastName: last, email: newEmail.trim(), init: ((first[0] || "") + (last[0] || "")).toUpperCase() || first.slice(0, 2).toUpperCase(), color: T.tGreen }]);
    setNewFirst(""); setNewLast(""); setNewEmail("");
  };
  const addSegment = (s: { id: string; name: string; count: number; color?: string }) => {
    if (addedSegs.has(s.id)) return;
    setList([...list, { uid: uid(), kind: "segment", id: s.id, name: s.name, count: s.count, color: s.color }]);
  };

  const handleImportFile = (file?: File | null) => {
    if (!file) return;
    setImportErr("");
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const text = String(reader.result || "");
        const rows = text.split(/\r?\n/).map((r) => r.trim()).filter(Boolean).map((r) => r.split(/[\t,]/).map((c) => c.trim().replace(/^"|"$/g, "")));
        if (!rows.length) { setImportErr("File looks empty."); return; }
        let start = 0, nameI = 0, emailI = 1, compI = 2, firstI = -1, lastI = -1;
        const head = rows[0].map((c) => c.toLowerCase());
        if (head.some((h) => /name|email|company|first|last/.test(h))) {
          start = 1;
          firstI = head.findIndex((h) => /first/.test(h));
          lastI = head.findIndex((h) => /last|surname/.test(h));
          const ni = head.findIndex((h) => /^name|full ?name/.test(h)); if (ni >= 0) nameI = ni;
          const ei = head.findIndex((h) => /email/.test(h)); if (ei >= 0) emailI = ei;
          const ci = head.findIndex((h) => /company|org/.test(h)); if (ci >= 0) compI = ci;
        }
        const added: GuestEntry[] = [];
        for (let i = start; i < rows.length; i++) {
          const cells = rows[i];
          const first = firstI >= 0 ? cells[firstI] || "" : "";
          const last = lastI >= 0 ? cells[lastI] || "" : "";
          const name = (first || last) ? `${first} ${last}`.trim() : (cells[nameI] || "").trim();
          if (!name) continue;
          const fn = first || name.split(/\s+/)[0] || "";
          const ln = last || name.split(/\s+/).slice(1).join(" ") || "";
          added.push({ uid: uid(), kind: "new", name, firstName: fn, lastName: ln, email: (cells[emailI] || "").trim(), company: (cells[compI] || "").trim(), init: ((fn[0] || "") + (ln[0] || "")).toUpperCase() || name.slice(0, 2).toUpperCase(), color: T.tOrange, imported: true });
        }
        if (!added.length) { setImportErr("No names found. Use a CSV with name, email columns."); return; }
        setList([...list, ...added]);
      } catch { setImportErr("Couldn't read that file. Use a CSV with name, email, company columns."); }
    };
    reader.readAsText(file);
  };

  const individuals = list.filter((e) => e.kind !== "segment").length;
  const segPeople = list.filter((e) => e.kind === "segment").reduce((n, e) => n + (e.count || 0), 0);
  const total = individuals + segPeople;

  const MODES = [
    { id: "customers", label: t("events.creationCreateEventGuestList.lookUpLblCustomersCustomersTolowercase.icu", { value0: lbl("customers", t("common.customers", "Customers")).toLowerCase() }), icon: <Icon.search size={13} /> },
    { id: "new", label: t("events.creationCreateEventGuestList.addNewLblCustomerCustomerTolowercase.icu", { value0: lbl("customer", t("common.customer", "Customer")).toLowerCase() }), icon: <Icon.plus size={13} /> },
    { id: "segment", label: t("events.creationCreateEventGuestList.addASegment", "Add a segment"), icon: <Icon.layers size={13} /> },
    { id: "import", label: t("events.creationCreateEventGuestList.importAList", "Import a list"), icon: <Icon.upload size={13} /> },
  ] as const;

  return (
    <CreationShell step={6}>
      <WizardCanvas width="wide" cols="split">
        {/* Left: add panel */}
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <FormSection title={t("events.creationCreateEventGuestList.guestList", "Guest list")} subtitle={t("events.creationCreateEventGuestList.addThePeopleYouWantAtThisEvent", "Add the people you want at this event.")}>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {MODES.map((m) => {
                const on = mode === m.id;
                return <Btn key={m.id} type="button" onClick={() => setMode(m.id as typeof mode)} style={{ display: "inline-flex", alignItems: "center", gap: 8, height: "auto", padding: "8px 14px", borderRadius: 999, border: `1.5px solid ${on ? T.p1 : T.n4}`, background: on ? T.p4 : T.white, color: on ? T.p1 : T.n1, fontFamily: T.font, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>{m.icon}{m.label}</Btn>;
              })}
            </div>

            {mode === "customers" ? (
              <div>
                <Input iconLeft={<Icon.search size={14} />} value={q} onChange={(e: ChangeEvent<HTMLInputElement>) => setQ(e.target.value)} placeholder={t("events.creationCreateEventGuestList.searchByNameEmailOrCompany", "Search by name, email or company…")} />
                {searching ? <div style={{ padding: 12, fontSize: 13, color: T.soft }}>{t("events.creationCreateEventGuestList.searchingU2026", "Searching\\u2026")}</div> : null}
                {!searching && q.trim() && results.length === 0 ? <div style={{ padding: 12, fontSize: 13, color: T.soft }}>{/* TODO(i18n): replace template-literal with t('events.creationCreateEventGuestList.text', { ... }) */`No ${lbl("customers", t("common.customers", "Customers")).toLowerCase()} found.`}</div> : null}
                {q.trim() && results.length > 0 ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 8 }}>
                    {results.map((g) => (
                      <div key={g.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", background: T.n6, borderRadius: 6 }}>
                        <Avatar size={28} initials={g.init || (g.name || "?").slice(0, 2).toUpperCase()} color={g.color || T.p1} />
                        <div style={{ flex: 1 }}><div style={{ fontSize: 13, fontWeight: 600 }}>{g.name}</div><div style={{ fontSize: 11, color: T.soft }}>{g.email}</div></div>
                        <Btn size="sm" variant="secondary" icon={<Icon.plus size={12} />} onClick={() => addCustomer(g)} disabled={addedIds.has(g.id)}>{t("common.actions.add", "Add")}</Btn>
                      </div>
                    ))}
                  </div>
                ) : null}
                {!q.trim() ? <div style={{ padding: 12, fontSize: 13, color: T.soft, textAlign: "center", border: `1px dashed ${T.n4}`, borderRadius: 6 }}>{/* TODO(i18n): replace template-literal with t('events.creationCreateEventGuestList.text2', { ... }) */`Type to search existing ${lbl("customers", t("common.customers", "Customers")).toLowerCase()}.`}</div> : null}
              </div>
            ) : mode === "new" ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                  <Field label={t("events.creationCreateEventGuestList.firstName", "First name *")}><Input value={newFirst} onChange={(e: ChangeEvent<HTMLInputElement>) => setNewFirst(e.target.value)} placeholder={t("events.creationCreateEventGuestList.jane", "Jane")} /></Field>
                  <Field label={t("events.creationCreateEventGuestList.lastName", "Last name")}><Input value={newLast} onChange={(e: ChangeEvent<HTMLInputElement>) => setNewLast(e.target.value)} placeholder={t("events.creationCreateEventGuestList.smith", "Smith")} /></Field>
                </div>
                <Field label={t("events.creationCreateEventGuestList.email", "Email")}><Input type="email" value={newEmail} onChange={(e: ChangeEvent<HTMLInputElement>) => setNewEmail(e.target.value)} placeholder={t("events.creationCreateEventGuestList.janeExampleCom", "jane@example.com")} /></Field>
                <Btn disabled={!newFirst.trim()} icon={<Icon.plus size={14} />} onClick={addNew}>{t("events.creationCreateEventGuestList.addToGuestList", "Add to guest list")}</Btn>
              </div>
            ) : mode === "segment" ? (
              <div>
                {allSegments.length === 0 ? (
                  <div style={{ padding: 16, fontSize: 13, color: T.soft, textAlign: "center", border: `1px dashed ${T.n4}`, borderRadius: 6 }}>{t("events.creationCreateEventGuestList.segmentsWireWhenSegmentsListIsConnected", "Segments wire when segments.list is connected.")}</div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {allSegments.map((s) => (
                      <div key={s.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", background: T.n6, borderRadius: 6 }}>
                        <span style={{ width: 28, height: 28, borderRadius: 999, background: s.color || T.p1, color: T.white, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700 }}>{t("events.creationCreateEventGuestList.s", "S")}</span>
                        <div style={{ flex: 1 }}><div style={{ fontSize: 13, fontWeight: 600 }}>{s.name}</div><div style={{ fontSize: 11, color: T.soft }}>{s.count.toLocaleString()} {t("events.creationCreateEventGuestList.people", "people")}</div></div>
                        <Btn size="sm" variant="secondary" icon={<Icon.plus size={12} />} onClick={() => addSegment(s)} disabled={addedSegs.has(s.id)}>{t("common.actions.add", "Add")}</Btn>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div>
                <label onDrop={(e) => { e.preventDefault(); handleImportFile(e.dataTransfer.files[0]); }} onDragOver={(e) => e.preventDefault()} onClick={() => fileRef.current?.click()} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8, padding: 24, border: `1.5px dashed ${T.n4}`, borderRadius: 8, cursor: "pointer", textAlign: "center" }}>
                  <Icon.upload size={20} />
                  <div style={{ fontSize: 13, color: T.n1 }}>{t("events.creationCreateEventGuestList.dragACsvHereOr", "Drag a CSV here, or")}{" "}<span style={{ color: T.p1, fontWeight: 600 }}>{t("events.creationCreateEventGuestList.browse", "browse")}</span></div>
                  <div style={{ fontSize: 11, color: T.soft }}>{t("events.creationCreateEventGuestList.nameEmailCompanyColumns", "name, email, company columns")}</div>
                  {/* ui-raw: hidden native file picker triggered programmatically via ref; Input models no file type */}
                  <input ref={fileRef} type="file" accept=".csv,.txt,.tsv" onChange={(e) => handleImportFile(e.target.files?.[0])} style={{ display: "none" }} />
                </label>
                {importErr ? <div style={{ fontSize: 12, color: T.red1, marginTop: 6 }}>{importErr}</div> : null}
              </div>
            )}
          </FormSection>
        </div>

        {/* Right: assembled list */}
        <div>
          <Card style={{ padding: 0, overflow: "hidden" }}>
            <div style={{ padding: "12px 16px", borderBottom: `1px solid ${T.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: T.n1 }}>{t("events.creationCreateEventGuestList.guestList", "Guest list")}</span>
              <span style={{ fontSize: 12, color: T.soft }}>{total.toLocaleString()} {total === 1 ? t('events.creationCreateEventGuestList.text3') : t('events.creationCreateEventGuestList.text4')}</span>
            </div>
            {list.length === 0 ? (
              <div style={{ padding: 24, textAlign: "center", color: T.soft, fontSize: 13 }}>{t("events.creationCreateEventGuestList.noGuestsAddedYet", "No guests added yet.")}</div>
            ) : (
              <div style={{ maxHeight: 440, overflow: "auto" }}>
                {list.map((e) => (
                  <div key={e.uid} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", borderBottom: `1px solid ${T.border}` }}>
                    {e.kind === "segment"
                      ? <span style={{ width: 28, height: 28, borderRadius: 999, background: e.color || T.p1, color: T.white, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, flexShrink: 0 }}>{t("events.creationCreateEventGuestList.s", "S")}</span>
                      : <Avatar size={28} initials={(e.init || (e.name || "?").slice(0, 2)).toUpperCase()} color={e.color || T.p1} />}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: T.n1 }}>{e.name}</div>
                      <div style={{ fontSize: 11, color: T.soft }}>{e.kind === "segment" ? /* TODO(i18n): replace template-literal with t('events.creationCreateEventGuestList.text5', { ... }) */`Segment · ${(e.count || 0).toLocaleString()} people` : e.email || (e.kind === "new" ? `New ${lbl("customer", t("common.customer", "Customer")).toLowerCase()}` : `Existing ${lbl("customer", "Customer").toLowerCase()}`)}</div>
                    </div>
                    <Btn variant="ghost" iconOnly={<Icon.x size={13} />} type="button" onClick={() => remove(e.uid)} title={t("common.actions.remove", "Remove")} style={{ width: "auto", height: "auto", background: "transparent", border: "none", cursor: "pointer", color: T.soft, display: "inline-flex", padding: 4 }} onMouseEnter={(el: MouseEvent<HTMLButtonElement>) => el.currentTarget.style.color = T.red1} onMouseLeave={(el: MouseEvent<HTMLButtonElement>) => el.currentTarget.style.color = T.soft} />
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      </WizardCanvas>
    </CreationShell>
  );
}
